import styled, { css } from 'styled-components';
import Select from 'react-select';
import { rem } from 'polished';
import { getCSSArrow } from '@whitelabel/helpers/style/utils';
import { zIndex } from '@whitelabel/helpers/style/zIndex';
import { fontStyle, fontSize } from '@whitelabel/helpers/style/type';
import { gray } from '@whitelabel/helpers/style/colors';
import { borderRadius } from '@whitelabel/helpers/style/box';
import { mediaBreakpointUp } from '@whitelabel/helpers/style/grid';
import { dropdownColors, inputColors } from '@whitelabel/global-theme/colors';

export const StyledSelect = styled(Select).attrs({ classNamePrefix: 'react-select' })<{
  $rtl: boolean;
  reverseSelectionArrow: boolean;
  $hasIcon?: boolean;
}>`
  .label-ui {
    font-size: ${rem(9)};
    font-weight: bold;
    padding-top: ${rem(4)};
  }

  .react-select__control {
    height: ${rem(43)};
    font-size: 1.125rem;
    border-color: ${inputColors.border.default};
    border-radius: ${borderRadius('base')};
    cursor: pointer;

    ${mediaBreakpointUp('lg')`
      font-size: ${fontSize('lg')};
      height: ${rem(46)};
    `}

    .react-select__single-value {
      color: ${inputColors.value.default};
    }

    .react-select__placeholder {
      color: ${gray(600)};
    }

    &:focus-within {
      border-color: ${inputColors.border.focus};
      box-shadow: none;

      .react-select__single-value {
        color: ${inputColors.value.focus};
      }
    }
  }

  .react-select__control--is-disabled {
    background-color: ${inputColors.background.disabled};
    border-color: ${inputColors.border.disabled};
  }

  .react-select__single-value {
    width: 100%;
  }

  .react-select__single-value--is-disabled {
    color: ${inputColors.value.disabled};
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    padding-inline-end: 1rem;

    svg {
      color: ${inputColors.icon.default};
    }
  }

  .react-select__value-container {
    padding-inline-start: ${(props) => (props.$hasIcon ? '2.5rem' : '0.75rem')};

    ${mediaBreakpointUp('md')`
      padding-inline-start: ${(props: { $hasIcon: boolean }) => (props.$hasIcon ? '2.75rem' : '1rem')};
    `}
  }

  .react-select__control,
  .react-select__control--is-focused,
  .react-select__control--menu-is-opened {
    ${({ invalid }) =>
      invalid &&
      css`
        border-color: ${inputColors.border.error};
      `}

    &:hover {
      border-color: ${inputColors.border.hover};

      ${({ invalid }) =>
        invalid &&
        css`
          border-color: ${inputColors.border.error};
        `}
    }
  }

  .react-select__menu {
    ${fontStyle('formValue')}
    z-index: ${zIndex('contentLayer')};
    margin-top: 0.75rem;
    ${({ reverseSelectionArrow }) => getCSSArrow(-20, 13, 'default', reverseSelectionArrow)};

    .react-select__option--is-focused {
      background-color: ${dropdownColors.focus};
    }

    .react-select__option--is-selected {
      color: ${inputColors.label.default};
      background-color: ${dropdownColors.selected};
    }
  }

  /* stylelint-disable-next-line order/order */
  ${({ isMulti }) =>
    isMulti &&
    css`
      .react-select__control.react-select__control {
        height: auto;
        min-height: ${rem(43)};
        flex-direction: row;

        ${mediaBreakpointUp('lg')`
          min-height: ${rem(46)};
        `}

        .react-select__clear-indicator {
          padding-top: 0rem;
          padding-bottom: 0rem;

          svg {
            color: ${gray(600)};
          }
        }

        .react-select__indicators {
          margin: 0;
        }
      }

      .react-select__value-container--is-multi.react-select__value-container--has-value
        + .react-select__indicators
        .react-select__indicator-separator {
        display: block;
      }

      .react-select__value-container--has-value {
        gap: 8px;
        padding-top: 8px;
        padding-bottom: 8px;

        /* This is to remove the padding and vertical margin on the input cursor */
        div {
          &:not(div[class='react-select__multi-value']) {
            padding: 0;
            margin: 0;
          }
        }
      }

      .react-select__dropdown-indicator {
        padding-top: 0;
        padding-bottom: 0;
      }

      .react-select__value-container--is-multi {
        padding-inline-start: 2.75rem;

        .react-select__multi-value {
          background: ${gray(300)};
          border-radius: 3px;
          border: none;
          margin: 0;
          align-content: center;
          height: 30px;

          .react-select__multi-value__label {
            color: ${gray(700)};
            padding: 0;
            padding-inline-start: 0.5rem;
            font-size: ${rem(18)};
            line-height: 27px;

            ${mediaBreakpointUp('lg')`
              font-size: ${rem(20)};
              line-height: 30px;
            `}
          }

          .react-select__multi-value__remove {
            padding-inline-start: ${rem(4)};
            padding-inline-end: ${rem(4)};
            svg {
              color: ${inputColors.icon.default};
              padding: 0.25rem;
              width: 1.625rem;
              height: 1.625rem;
              border-radius: 1rem;
              transition: color 0.2s ease-out, background-color 0.2s ease-out;
            }

            &:hover {
              background: none;
              color: inherit;

              svg {
                color: ${gray(700)};
                background-color: inherit;
              }
            }
          }
        }
      }
    `}
`;

export const StyledCustomIcon = (
  Comp: React.FunctionComponent<React.PropsWithChildren<React.SVGProps<SVGSVGElement>>>,
) => styled(Comp)`
  position: absolute;
  height: 1rem;
  width: 1rem;
  inset-inline-start: 1rem;
  top: 1rem;
  color: ${gray(600)};
  z-index: ${zIndex('content')};
`;

export const StyledRelativeWrapper = styled.div`
  position: relative;

  .react-select__primary-icon {
    top: 50%;
    transform: translateY(-50%);
  }
`;
